import React, { useEffect, useState } from "react";

import { Error, Loading } from "./states";

import { Bar } from "react-chartjs-2";

const StatsBlock = ({ stats, wind_river }) => {
  return (
    <div className="chart__stats">
      <div>
        <strong>
          {wind_river == "River Level"
            ? "Avg. River Level (m)"
            : "Avg. Wind Speed (m/s)"}
        </strong>
        <span>{stats.wind_river}</span>
      </div>
      <div>
        <strong>Prod + Curtail (kWh)</strong>
        <span>{stats.prod_curtail}</span>
      </div>
      <div>
        <strong>Rated Capacity (MW)</strong>
        <span>{stats.rated_capacity}</span>
      </div>
      <div>
        <strong>Capacity Factor (%)</strong>
        <span>{stats.capacity_factor}</span>
      </div>
    </div>
  );
};

const WeeklyStats = ({ wind_river, data, title, stats }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const sortData = (data) => {
      try {
        if (!data) {
          throw new Error("No data found");
        }

        const groupedArray = Object.entries(
          data.reduce((acc, item) => {
            const { week_num, ...rest } = item;
            if (!acc[week_num]) acc[week_num] = [];
            acc[week_num].push(rest);
            return acc;
          }, {}),
        ).map(([week_num, data]) => ({
          week_num: Number(week_num),
          data,
        }));

        const groupedStats = stats.reduce((acc, item) => {
          acc[item.week_num] = item;
          return acc;
        }, {});

        // Merge stats into groupedArray
        const mergedData = groupedArray.map((item) => ({
          ...item,
          stats: groupedStats[item.week_num] || null,
        }));

        setAllData(mergedData);
        setActiveIndex(mergedData.length - 1);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    sortData(data);

    return () => {};
  }, [data]);

  useEffect(() => {
    setLoading(true);

    const setActiveData = ({ data, selected }) => {
      try {
        const latestData = data[selected];
        setChartData({
          week: latestData.week_num,
          data: {
            labels: latestData["data"].map((item) => item.date),
            datasets: [
              {
                label: wind_river,
                data: latestData["data"].map((item) => item.wind_river),
                type: "line",
                backgroundColor: "rgba(255, 99, 132, 1)",
                borderColor: "rgba(255, 99, 132, 1)",
                yAxisID: "y2",
              },
              {
                label: "Production",
                data: latestData["data"].map((item) => item.production_value),
                titleColor: "rgba(255,255,255,1)",
                backgroundColor: "rgba(39, 163, 52, 1)",
                yAxisID: "y1",
              },
              {
                label: "Theoretical",
                data: latestData["data"].map((item) => item.theoretical_value),
                backgroundColor: "rgba(28, 63, 130, 1)",
                yAxisID: "y1",
              },
            ],
          },
          stats: latestData.stats,
        });
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    if (allData) {
      setActiveData({ data: allData, selected: activeIndex });
    }

    return () => {};
  }, [activeIndex]);

  const handleIndexChange = (direction) => {
    if (direction === "up") {
      setActiveIndex((prev) => prev + 1);
    } else {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y1: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Production (kWh)",
        },
        grid: {
          drawOnChartArea: true, // Ensures grid lines are drawn for this axis
        },
      },
      y2: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text:
            wind_river == "Wind Speed" ? "Wind Speed (m/s)" : "River Level (m)",
        },
        suggestedMin: 0,
        suggestedMax: 16,
        grid: {
          drawOnChartArea: false, // Ensures grid lines are drawn for this axis
        },
      },
    },
    title: {
      display: true,
      text: "Bar Chart Data",
    },
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <>
      <span className="h3">{title}</span>
      <div className="component__graph-switcher">
        <div className="nav-item">
          {activeIndex > 0 && (
            <span onClick={() => handleIndexChange("down")}>Prev</span>
          )}
        </div>
        <div className="current">Week {chartData.week}</div>
        <div className="nav-item">
          {activeIndex < allData.length - 1 && (
            <span onClick={() => handleIndexChange("up")}>Next</span>
          )}
        </div>
      </div>
      {chartData && <Bar data={chartData.data} options={options} />}
      {chartData.stats && (
        <StatsBlock stats={chartData.stats} wind_river={wind_river} />
      )}
    </>
  );
};

export default WeeklyStats;
